import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Progress,
  Table,
} from 'reactstrap';
import {
  Button,
  Icon,
} from '../../components/Component';
import { getTemporalesMro, updateTemporalesMro, insertTemporalesMro } from '../temporales';
import Swal from 'sweetalert2';

const TemporalesExcelUpload = ({ isOpen, toggle, onUploadSuccess }) => {
  // Estados principales
  const [file, setFile] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mappedData, setMappedData] = useState([]);
  const [existingRecords, setExistingRecords] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [processStep, setProcessStep] = useState('upload'); // upload, preview, processing
  const [validationResults, setValidationResults] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  const user = JSON.parse(localStorage.getItem('user'));

  // Mapeo de campos de Excel a BD
  const fieldMappings = {
    'ID': 'id',
    'Id': 'id',
    'id': 'id',
    'ID Temporal': 'id',
    'Identificador': 'id',
    'PO /centro de costos': 'po_number',
    'PO': 'po_number',
    'Centro de costos': 'po_number',
    'N° de PO': 'po_number',
    'Responsable usuario del material': 'responsible',
    'Responsable': 'responsible',
    'Usuario del material': 'responsible',
    'Vendor': 'vendor',
    'Proveedor': 'vendor',
    'MERCADERIA': 'mercaderia_descripcion',
    'Mercadería': 'mercaderia_descripcion',
    'Descripción': 'mercaderia_descripcion',
    'Destino': 'destination',
    'Destination': 'destination',
    'Peso bruto': 'peso_bruto',
    'Peso Bruto': 'peso_bruto',
    'Peso neto': 'peso_neto',
    'Peso Neto': 'peso_neto',
    'Medidas del embalaje': 'medidas_embalaje',
    'Medidas': 'medidas_embalaje',
    'Embalaje': 'medidas_embalaje',
    'Número de serie': 'serial',
    'Serie': 'serial',
    'Serial': 'serial',
    'Origen de la mercadería': 'origen_mercaderia',
    'Origen': 'origen_mercaderia',
    'Marca y modelo': 'marca_modelo',
    'Marca': 'marca_modelo',
    'Modelo': 'marca_modelo',
    'Valor a declarar': 'valor_declarar',
    'Valor declarar': 'valor_declarar',
    'Valor de la reparacion': 'valor_reparacion',
    'Valor reparación': 'valor_reparacion',
    'Fecha de aprobacion del expediente': 'aprobation_date',
    'Fecha aprobación': 'aprobation_date',
    'Aprobación': 'aprobation_date',
    'Dias habilitados por SICNEA': 'sicnea_days',
    'Días SICNEA': 'sicnea_days',
    'SICNEA': 'sicnea_days',
    'Fecha de embarque salida': 'fecha_embarque',
    'Fecha embarque': 'fecha_embarque',
    'Embarque': 'fecha_embarque',
    'Fecha de primer vencimiento temporal': 'fecha_vencimiento_temporal',
    'Vencimiento temporal': 'fecha_vencimiento_temporal',
    'Fecha autorización de prórroga': 'fecha_aprobacion_prorroga',
    'Prórroga': 'fecha_aprobacion_prorroga',
    'Fecha vencimiento de prórroga': 'due_date',
    'Vencimiento': 'due_date',
    'Fecha vencimiento': 'due_date',
    'ET': 'ET',
    'IC': 'IC',
    'FECHA DE RETORNO': 'fecha_retorno',
    'Fecha retorno': 'fecha_retorno',
    'Retorno': 'fecha_retorno',
    'Estado': 'status',
    'Status': 'status',
    'COMMENTS': 'comentario',
    'Comentarios': 'comentario',
    'Comentario': 'comentario',
  };

  // Cargar registros existentes al abrir el modal
  useEffect(() => {
    if (isOpen) {
      loadExistingRecords();
    }
  }, [isOpen]);

  const loadExistingRecords = async () => {
    try {
      const data = await getTemporalesMro();
      setExistingRecords(data); // Guardamos todos los registros, no solo los IDs
    } catch (error) {
      console.error('Error loading existing records:', error);
    }
  };

  // Todas las columnas son OBLIGATORIAS
  const requiredColumns = [
    'ID',
    'PO /centro de costos',
    'Responsable usuario del material',
    'Vendor',
    'MERCADERIA',
    'Destino',
    'Peso bruto',
    'Peso neto',
    'Medidas del embalaje',
    'Número de serie',
    'Origen de la mercadería',
    'Marca y modelo',
    'Valor a declarar',
    'Valor de la reparacion',
    'Fecha de aprobacion del expediente',
    'Dias habilitados por SICNEA',
    'Fecha de embarque salida',
    'Fecha de primer vencimiento temporal',
    'Fecha autorización de prórroga',
    'Fecha vencimiento de prórroga',
    'ET',
    'IC',
    'FECHA DE RETORNO',
    'Estado',
    'COMMENTS'
  ];

  // Ya no hay columnas recomendadas - todas son obligatorias
  const recommendedColumns = [];

  // Validar formato de Excel
  const validateExcelFormat = (excelHeaders, jsonData) => {
    // Verificar columnas requeridas
    const missingRequired = requiredColumns.filter(requiredCol => 
      !excelHeaders.some(header => {
        const cleanHeader = header.trim();
        // Buscar coincidencia exacta o mapeo
        return cleanHeader === requiredCol || 
               fieldMappings[cleanHeader] === fieldMappings[requiredCol];
      })
    );

    if (missingRequired.length > 0) {
      return {
        isValid: false,
        missingRequired,
        message: `El archivo Excel no tiene el formato correcto. Faltan ${missingRequired.length} columnas obligatorias de las ${requiredColumns.length} requeridas.`
      };
    }

    // Verificar que tenga al menos 1 fila de datos
    if (jsonData.length === 0) {
      return {
        isValid: false,
        message: 'El archivo Excel no contiene filas de datos.'
      };
    }

    // Verificar que las filas tengan datos en las columnas principales
    const firstRow = jsonData[0];
    const hasMainData = excelHeaders.some(header => {
      const mappedField = fieldMappings[header.trim()];
      return (mappedField === 'po_number' || mappedField === 'responsible' || mappedField === 'vendor') 
        && firstRow[header] && firstRow[header].toString().trim() !== '';
    });

    if (!hasMainData) {
      return {
        isValid: false,
        message: 'Las filas de datos parecen estar vacías en las columnas principales (PO, Responsable, Vendor).'
      };
    }

    return {
      isValid: true
    };
  };

  // Manejar selección de archivo
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      const fileSizeInMB = selectedFile.size / (1024 * 1024);
      
      // Validar extensión
      if (!['xlsx', 'xls'].includes(fileExtension)) {
        Swal.fire({
          icon: 'error',
          title: 'Formato de archivo incorrecto',
          html: `
            <div style="text-align: left;">
              <p>Solo se permiten archivos de Excel:</p>
              <ul>
                <li>✅ <strong>.xlsx</strong> (Excel 2007 o superior)</li>
                <li>✅ <strong>.xls</strong> (Excel 97-2003)</li>
              </ul>
              <p style="color: #dc3545; margin-top: 15px;">
                <strong>Archivo recibido:</strong> .${fileExtension}
              </p>
            </div>
          `,
          confirmButtonText: 'Entendido'
        });
        e.target.value = ''; // Limpiar input
        return;
      }
      
      // Validar tamaño de archivo (máximo 10MB)
      if (fileSizeInMB > 10) {
        Swal.fire({
          icon: 'warning',
          title: 'Archivo muy grande',
          html: `
            <p>El archivo es demasiado grande (${fileSizeInMB.toFixed(2)} MB)</p>
            <p><strong>Tamaño máximo permitido:</strong> 10 MB</p>
            <p><strong>Recomendación:</strong> Reduce el número de registros o usa múltiples archivos.</p>
          `,
          confirmButtonText: 'Entendido'
        });
        e.target.value = ''; // Limpiar input
        return;
      }
      
      setFile(selectedFile);
      readExcelFile(selectedFile);
    }
  };

  // Leer archivo Excel
  const readExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        if (jsonData.length > 0) {
          const excelHeaders = Object.keys(jsonData[0]);
          
          // Validar formato antes de proceder
          const formatValidation = validateExcelFormat(excelHeaders, jsonData);
          
          if (!formatValidation.isValid) {
            Swal.fire({
              icon: 'error',
              title: 'Formato de Excel Incorrecto',
              html: `
                <div style="text-align: left;">
                  <p><strong>Error:</strong> ${formatValidation.message}</p>
                  
                  ${formatValidation.missingRequired ? `
                    <div style="margin: 15px 0;">
                      <p><strong>Columnas faltantes (${formatValidation.missingRequired.length}):</strong></p>
                      <ul style="color: #dc3545; font-weight: bold;">
                        ${formatValidation.missingRequired.map(col => `<li><code>${col}</code></li>`).join('')}
                      </ul>
                    </div>
                  ` : ''}
                  
                  <div style="margin: 15px 0;">
                    <p><strong>TODAS las columnas son obligatorias (${requiredColumns.length} total):</strong></p>
                    <div style="max-height: 200px; overflow-y: auto; border: 1px solid #dee2e6; padding: 10px; background: #f8f9fa;">
                      <ul style="font-size: 11px; color: #495057; columns: 2; margin: 0;">
                        ${requiredColumns.map(col => `<li style="margin-bottom: 2px;"><code>${col}</code></li>`).join('')}
                      </ul>
                    </div>
                  </div>
                  
                  <div style="margin: 15px 0; padding: 10px; background: #fff3cd; border-left: 4px solid #ffc107; border-radius: 4px;">
                    <strong>⚠️ IMPORTANTE:</strong> Tu archivo debe tener EXACTAMENTE estas ${requiredColumns.length} columnas con los nombres mostrados arriba.
                  </div>
                  
                  <div style="margin: 15px 0; padding: 10px; background: #e7f3ff; border-left: 4px solid #2196F3; border-radius: 4px;">
                    <strong>💡 Solución:</strong> Descarga la plantilla oficial para asegurar el formato correcto.
                  </div>
                </div>
              `,
              width: 700,
              confirmButtonText: 'Entendido'
            });
            
            // Resetear el input de archivo
            setFile(null);
            const fileInput = document.querySelector('input[type="file"]');
            if (fileInput) fileInput.value = '';
            
            return;
          }
          
          setHeaders(excelHeaders);
          setExcelData(jsonData);
          autoMapColumns(excelHeaders);
          setProcessStep('preview');
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'El archivo Excel está vacío o no tiene datos válidos',
          });
        }
      } catch (error) {
        console.error('Error reading Excel file:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Error al leer el archivo Excel. Asegúrate de que el archivo no esté corrupto.',
        });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Mapeo automático de columnas
  const autoMapColumns = (excelHeaders) => {
    const mapping = {};
    
    excelHeaders.forEach(header => {
      const cleanHeader = header.trim();
      // Buscar coincidencia exacta primero
      if (fieldMappings[cleanHeader]) {
        mapping[header] = fieldMappings[cleanHeader];
      } else {
        // Buscar coincidencia parcial (case insensitive)
        const foundMapping = Object.keys(fieldMappings).find(key => 
          key.toLowerCase().includes(cleanHeader.toLowerCase()) ||
          cleanHeader.toLowerCase().includes(key.toLowerCase())
        );
        if (foundMapping) {
          mapping[header] = fieldMappings[foundMapping];
        }
      }
    });
    
    setColumnMapping(mapping);
    mapExcelData(mapping);
  };

  // Mapear datos del Excel con validación
  const mapExcelData = (mapping) => {
    const mapped = excelData.map((row, index) => {
      const mappedRow = { originalIndex: index };
      
      // Mapear cada campo
      Object.keys(mapping).forEach(excelColumn => {
        const dbField = mapping[excelColumn];
        let value = row[excelColumn];
        
        // Formatear fechas si es necesario
        if (dbField.includes('fecha') || dbField === 'aprobation_date' || dbField === 'due_date') {
          value = formatExcelDate(value);
        }
        
        // Formatear números
        if (['peso_bruto', 'peso_neto', 'valor_declarar', 'valor_reparacion', 'sicnea_days'].includes(dbField)) {
          value = parseFloat(value) || 0;
        }
        
        mappedRow[dbField] = value;
      });
      
      return mappedRow;
    });
    
    setMappedData(mapped);
    validateMappedData(mapped);
  };

  // Formatear fechas de Excel
  const formatExcelDate = (excelDate) => {
    if (!excelDate) return null;
    
    try {
      // Si es un número (fecha de Excel)
      if (typeof excelDate === 'number') {
        const date = XLSX.SSF.parse_date_code(excelDate);
        return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
      }
      
      // Si es texto, intentar parsearlo
      const date = new Date(excelDate);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
      
      return null;
    } catch (error) {
      console.error('Error formatting date:', error);
      return null;
    }
  };

  // Validar datos mapeados
  const validateMappedData = (mapped) => {
    const validation = mapped.map((row, index) => {
      const issues = [];
      
      // Validar que tenga ID o PO
      if (!row.id && !row.po_number) {
        issues.push('ID o PO requerido');
      }
      
      // Verificar si el registro existe en BD
      let existingRecord = null;
      let recordExists = false;
      let action = 'insert';
      
      if (row.id) {
        // Buscar por ID primero (más preciso)
        existingRecord = existingRecords.find(record => record.id == row.id);
        if (existingRecord) {
          recordExists = true;
          action = 'update';
        }
      } else if (row.po_number) {
        // Si no hay ID, buscar por PO como fallback
        existingRecord = existingRecords.find(record => record.po_number === row.po_number);
        if (existingRecord) {
          recordExists = true;
          action = 'update';
          // Asignar el ID del registro existente para la actualización
          row.id = existingRecord.id;
        }
      }
      
      return {
        index,
        id: row.id,
        po_number: row.po_number,
        recordExists,
        existingRecord,
        issues,
        isValid: issues.length === 0,
        action
      };
    });
    
    setValidationResults(validation);
  };

  // Procesar y cargar datos
  const processData = async () => {
    setProcessStep('processing');
    setUploadProgress(0);
    
    const validRows = validationResults.filter(v => v.isValid);
    let processed = 0;
    let errors = [];
    
    try {
      for (const validation of validRows) {
        const rowData = mappedData[validation.index];
        
        try {
          if (validation.action === 'update') {
            // Actualizar registro existente usando el ID
            await updateTemporalesMro(rowData, user);
          } else {
            // Insertar nuevo registro (sin ID o con ID que no existe)
            const insertData = { ...rowData };
            if (insertData.id) {
              // Si hay ID pero no existe en BD, lo removemos para que la BD genere uno nuevo
              delete insertData.id;
            }
            await insertTemporalesMro(insertData, user);
          }
          
          processed++;
          setUploadProgress(Math.round((processed / validRows.length) * 100));
          
          // Pequeña pausa para mostrar progreso
          await new Promise(resolve => setTimeout(resolve, 100));
          
        } catch (error) {
          console.error(`Error processing record ID ${validation.id || 'N/A'} / PO ${validation.po_number}:`, error);
          errors.push({
            id: validation.id,
            po: validation.po_number,
            error: error.message || 'Error desconocido'
          });
        }
      }
      
      // Mostrar resultados
      if (errors.length === 0) {
        await Swal.fire({
          icon: 'success',
          title: '¡Éxito!',
          text: `Se procesaron correctamente ${processed} registros`,
        });
      } else {
        await Swal.fire({
          icon: 'warning',
          title: 'Procesamiento completado con errores',
          html: `
            <p>Registros procesados: ${processed}</p>
            <p>Errores: ${errors.length}</p>
            <details>
              <summary>Ver detalles de errores</summary>
              <ul style="text-align: left; margin-top: 10px;">
                ${errors.map(e => `<li>ID ${e.id || 'N/A'} / PO ${e.po}: ${e.error}</li>`).join('')}
              </ul>
            </details>
          `,
        });
      }
      
      // Refrescar datos y cerrar modal
      if (onUploadSuccess) {
        await onUploadSuccess();
      }
      
      resetComponent();
      toggle();
      
    } catch (error) {
      console.error('Error during processing:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error general durante el procesamiento',
      });
      
      setProcessStep('preview');
    }
  };

  // Resetear componente
  const resetComponent = () => {
    setFile(null);
    setExcelData([]);
    setHeaders([]);
    setMappedData([]);
    setColumnMapping({});
    setValidationResults([]);
    setUploadProgress(0);
    setProcessStep('upload');
  };

  // Generar plantilla Excel de ejemplo
  const downloadTemplate = () => {
    const templateData = [
      {
        'ID': 1,
        'PO /centro de costos': 'PO-2025-001',
        'Responsable usuario del material': 'Juan Pérez',
        'Vendor': 'ACME Corporation',
        'MERCADERIA': 'Bomba centrífuga modelo XYZ-100',
        'Destino': 'Planta Principal - Sector A',
        'Peso bruto': 150.5,
        'Peso neto': 125.0,
        'Medidas del embalaje': '100x80x60 cm',
        'Número de serie': 'SN123456789',
        'Origen de la mercadería': 'Estados Unidos',
        'Marca y modelo': 'ACME XYZ-100',
        'Valor a declarar': 25000.00,
        'Valor de la reparacion': 3500.00,
        'Fecha de aprobacion del expediente': '15/01/2025',
        'Dias habilitados por SICNEA': 180,
        'Fecha de embarque salida': '20/01/2025',
        'Fecha de primer vencimiento temporal': '20/07/2025',
        'Fecha autorización de prórroga': '',
        'Fecha vencimiento de prórroga': '20/01/2026',
        'ET': 'ET001',
        'IC': 'IC001',
        'FECHA DE RETORNO': '',
        'Estado': 'Activo',
        'COMMENTS': 'Reparación programada para Q2 2025'
      },
      {
        'ID': 2,
        'PO /centro de costos': 'PO-2025-002',
        'Responsable usuario del material': 'María García',
        'Vendor': 'Tech Solutions Inc',
        'MERCADERIA': 'Motor eléctrico trifásico 50HP',
        'Destino': 'Planta Norte - Línea 2',
        'Peso bruto': 85.2,
        'Peso neto': 78.5,
        'Medidas del embalaje': '80x60x50 cm',
        'Número de serie': 'MOT987654321',
        'Origen de la mercadería': 'Alemania',
        'Marca y modelo': 'Siemens 1LA7-113',
        'Valor a declarar': 12000.00,
        'Valor de la reparacion': 2800.00,
        'Fecha de aprobacion del expediente': '22/01/2025',
        'Dias habilitados por SICNEA': 120,
        'Fecha de embarque salida': '25/01/2025',
        'Fecha de primer vencimiento temporal': '25/05/2025',
        'Fecha autorización de prórroga': '20/05/2025',
        'Fecha vencimiento de prórroga': '25/09/2025',
        'ET': 'ET002',
        'IC': 'IC002',
        'FECHA DE RETORNO': '',
        'Estado': 'Pronto a vencer',
        'COMMENTS': 'Revisión de bobinado'
      },
      {
        'ID': '', // Ejemplo de registro nuevo sin ID
        'PO /centro de costos': 'PO-2025-003',
        'Responsable usuario del material': 'Carlos López',
        'Vendor': 'Global Parts',
        'MERCADERIA': 'Válvula de control automática',
        'Destino': 'Planta Sur - Área 3',
        'Peso bruto': 25.8,
        'Peso neto': 22.0,
        'Medidas del embalaje': '40x30x25 cm',
        'Número de serie': 'VAL456789123',
        'Origen de la mercadería': 'Italia',
        'Marca y modelo': 'Flowserve Series 2000',
        'Valor a declarar': 8500.00,
        'Valor de la reparacion': 1200.00,
        'Fecha de aprobacion del expediente': '10/02/2025',
        'Dias habilitados por SICNEA': 90,
        'Fecha de embarque salida': '15/02/2025',
        'Fecha de primer vencimiento temporal': '15/05/2025',
        'Fecha autorización de prórroga': '',
        'Fecha vencimiento de prórroga': '15/08/2025',
        'ET': 'ET003',
        'IC': 'IC003',
        'FECHA DE RETORNO': '',
        'Estado': 'Activo',
        'COMMENTS': 'Nuevo registro - dejar ID en blanco para insertar'
      }
    ];

    // Crear workbook y worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(templateData);
    
    // Agregar el worksheet al workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Temporales MRO');
    
    // Descargar archivo
    XLSX.writeFile(wb, 'Plantilla_Temporales_MRO.xlsx');
  };

  // Manejar cierre del modal
  const handleToggle = () => {
    if (processStep === 'processing') {
      return; // No permitir cerrar durante procesamiento
    }
    resetComponent();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} size="xl" style={{ maxWidth: '95%' }}>
      <ModalHeader toggle={handleToggle}>
        Cargar Temporales desde Excel
      </ModalHeader>
      <ModalBody style={{ minHeight: '500px' }}>
        {processStep === 'upload' && (
          <div>
            <div className="text-center mb-4">
              <Icon name="upload-cloud" style={{ fontSize: '4rem', color: '#007bff' }} />
              <h5 className="mt-3">Cargar Temporales desde Excel</h5>
              <p className="text-muted">El archivo debe seguir un formato específico para ser procesado correctamente</p>
            </div>

            {/* Sección de formato requerido */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-danger">
                  <div className="card-header bg-danger text-white">
                    <h6 className="mb-0">
                      <Icon name="alert-triangle" style={{ marginRight: '8px' }} />
                      TODAS las columnas son OBLIGATORIAS ({requiredColumns.length} columnas)
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="alert alert-warning mb-3" style={{ fontSize: '14px' }}>
                      <strong>⚠️ IMPORTANTE:</strong> Tu archivo Excel debe contener EXACTAMENTE todas estas {requiredColumns.length} columnas con los nombres mostrados abajo.
                    </div>
                    
                    <p className="mb-3"><strong>Columnas requeridas (nombres exactos):</strong></p>
                    <div className="row">
                      {requiredColumns.map((col, index) => (
                        <div key={index} className="col-md-6 col-lg-4 mb-2">
                          <div style={{ 
                            backgroundColor: '#f8d7da', 
                            padding: '8px', 
                            borderRadius: '4px',
                            border: '1px solid #f5c6cb',
                            fontSize: '12px'
                          }}>
                            <span style={{ color: '#dc3545', marginRight: '5px', fontWeight: 'bold' }}>
                              {index + 1}.
                            </span>
                            <code style={{ backgroundColor: 'transparent', color: '#721c24' }}>
                              {col}
                            </code>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="alert alert-info mt-3 mb-0" style={{ fontSize: '12px' }}>
                      <strong>💡 Consejos:</strong>
                      <ul className="mb-0 mt-1">
                        <li>Primera fila = encabezados (nombres exactos como arriba)</li>
                        <li>Fechas en formato: DD/MM/YYYY</li>
                        <li>Campo ID vacío = registro nuevo, con valor = actualizar existente</li>
                        <li>Sin celdas combinadas ni formato especial</li>
                        <li>Campos vacíos están permitidos (excepto los críticos)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <div className="mb-3">
                <Button 
                  color="success" 
                  size="lg" 
                  onClick={downloadTemplate}
                  className="mb-3"
                >
                  <Icon name="download" /> Descargar Plantilla Oficial
                </Button>
                <div style={{ fontSize: '12px', color: '#28a745', fontWeight: 'bold' }}>
                  ¡Recomendado! Usa esta plantilla para evitar errores de formato
                </div>
              </div>
              
              <div style={{ margin: '20px 0', padding: '1px', background: 'linear-gradient(90deg, #ddd 50%, transparent 50%)', backgroundSize: '10px 1px' }}></div>
              
              <div>
                <h6 style={{ color: '#666' }}>O selecciona tu archivo Excel</h6>
                <div className="alert alert-danger" style={{ maxWidth: '600px', margin: '0 auto 15px auto', fontSize: '13px' }}>
                  <Icon name="alert-triangle" style={{ color: '#dc3545', marginRight: '5px' }} />
                  <strong>IMPORTANTE:</strong> Tu archivo debe tener EXACTAMENTE las <strong>{requiredColumns.length} columnas obligatorias</strong> mostradas arriba
                </div>
                
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="form-control"
                  style={{ maxWidth: '400px', margin: '0 auto' }}
                />
                
                {file && (
                  <Alert color="info" className="mt-3">
                    <Icon name="file-text" style={{ marginRight: '8px' }} />
                    Archivo seleccionado: <strong>{file.name}</strong>
                  </Alert>
                )}
              </div>
            </div>
          </div>
        )}

        {processStep === 'preview' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6>Vista previa de datos ({mappedData.length} registros)</h6>
              <div>
                <Button color="secondary" size="sm" onClick={() => setProcessStep('upload')}>
                  <Icon name="arrow-left" /> Cambiar archivo
                </Button>
              </div>
            </div>

            {/* Resumen de validación */}
            <div className="row mb-3">
              <div className="col-md-3">
                <div className="p-3 bg-light rounded">
                  <strong>Total registros:</strong> {validationResults.length}
                </div>
              </div>
              <div className="col-md-3">
                <div className="p-3 bg-success text-white rounded">
                  <strong>Registros existentes (actualizar):</strong> {validationResults.filter(v => v.recordExists).length}
                </div>
              </div>
              <div className="col-md-3">
                <div className="p-3 bg-info text-white rounded">
                  <strong>Registros nuevos (insertar):</strong> {validationResults.filter(v => !v.recordExists).length}
                </div>
              </div>
              <div className="col-md-3">
                <div className="p-3 bg-warning text-dark rounded">
                  <strong>Errores de validación:</strong> {validationResults.filter(v => !v.isValid).length}
                </div>
              </div>
            </div>

            {/* Tabla de vista previa */}
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped size="sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>ID</th>
                    <th>PO</th>
                    <th>Responsable</th>
                    <th>Destino</th>
                    <th>Estado BD</th>
                    <th>Acción</th>
                    <th>Validación</th>
                  </tr>
                </thead>
                <tbody>
                  {validationResults.map((validation, index) => (
                    <tr key={index} className={validation.isValid ? '' : 'table-warning'}>
                      <td>{index + 1}</td>
                      <td>{validation.id || '-'}</td>
                      <td>{validation.po_number}</td>
                      <td>{mappedData[index]?.responsible || '-'}</td>
                      <td>{mappedData[index]?.destination || '-'}</td>
                      <td>
                        <span className={`badge ${validation.recordExists ? 'badge-success' : 'badge-primary'}`}>
                          {validation.recordExists ? 'Existe' : 'Nuevo'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${validation.action === 'update' ? 'badge-warning' : 'badge-info'}`}>
                          {validation.action === 'update' ? 'Actualizar' : 'Insertar'}
                        </span>
                      </td>
                      <td>
                        {validation.isValid ? (
                          <Icon name="check-circle" style={{ color: 'green' }} />
                        ) : (
                          <Icon name="alert-circle" style={{ color: 'orange' }} title={validation.issues.join(', ')} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        {processStep === 'processing' && (
          <div className="text-center">
            <h5>Procesando datos...</h5>
            <Progress value={uploadProgress} className="mt-3" />
            <p className="mt-2">{uploadProgress}% completado</p>
          </div>
        )}
      </ModalBody>
      
      <ModalFooter>
        {processStep === 'preview' && (
          <>
            <Button 
              color="primary" 
              onClick={processData}
              disabled={validationResults.filter(v => v.isValid).length === 0}
            >
              <Icon name="upload" /> Procesar datos ({validationResults.filter(v => v.isValid).length} válidos)
            </Button>
            <Button color="secondary" onClick={handleToggle}>
              Cancelar
            </Button>
          </>
        )}
        
        {processStep === 'processing' && (
          <Button color="secondary" disabled>
            Procesando...
          </Button>
        )}
        
        {processStep === 'upload' && (
          <Button color="secondary" onClick={handleToggle}>
            Cerrar
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default TemporalesExcelUpload;